import React, { useState } from 'react';
import { Menu, Button } from 'antd';
import styled from 'styled-components';

import withAuth from '~/libs/withAuth';
import { ContentLayout, MainBlockLayout } from '~/components/layouts';

import imgAssistantRent from '~/public/assistant-rent.svg';
import imgAssistantBank from '~/public/assistant-bank.svg';
import Image from 'next/image';
import Rent from './assistants/rent';
import useSWR from 'swr';
import useGlobalState from '~/libs/context';
import { addAssistant, deleteAssistant, updateAssistant } from '~/libs/requests';

const menuItems = [
  { key: 'dailyTasks', label: 'Daily Tasks' },
  { key: 'taxReturnTasks', label: 'Tax Return Tasks' }
];

const StyledAssistantCard = styled.div`
  flex: 1;
  max-width: 460px;
  background: #fff;
  border-radius: 8px;
  padding: 25px 20px;
  margin-bottom: 20px;
  margin-right: 20px;
  display: flex;

  .amount {
    float: right;
    font-size: 18px;
    color: #47b16c;
    margin-top: -10px;
  }
  
  h3 {
    margin: 10px 0 0 0;
  }

  &:last-child {
    margin-right: 0;
  }

  &:hover {
    cursor: pointer;
    box-shadow: 0 0 10px 0 rgba(0,0,0,0.1);
    transition: box-shadow 0.3s ease;
  }

  &.disabled {
    opacity: 0.45;
    pointer-events: none;
  }
`;

const StyledAssistantCards = styled.div`
  display: flex;
  flex-direction: row;

  img {
    margin-right: 20px;
  }

`;

const Assistants = () => {
  const { year } = useGlobalState();
  const [ section, setSection ] = useState('dailyTasks');
  const [ assistant, setAssistant ] = useState();

  const { data: assistantsData, isValidating: busyLoading, mutate: mutateAssistants } = 
    useSWR(`/api/assistants?year=${year}`);
  const assistants = assistantsData?.data ?? [];

  const { data: swrAccount } = useSWR('/api/account');
  const settings = swrAccount?.data?.settings ?? {};

  const jsxMenu = <Menu mode='horizontal' items={menuItems} selectedKeys={section}
    onClick={e => { 
      setAssistant();
      setSection(e.key);
    }}>
  </Menu>;

  const onAddAssistant = async (moduleId, year, data) => {
    const res = await addAssistant({ moduleId, year, data });
    if (res.success) {
      mutateAssistants();
    }
  };

  const onUpdateAssistant = async (assistantId, data) => {
    let realAssistant = assistants.find(a => a._id === assistantId);
    
    if (!data) {
      alert("No data.");
      console.error("No data.", { assistantId, data });
      return;
    }
    
    if (!realAssistant) {
      alert("Could not find assistant.");
      console.error("Could not find assistant.", { assistantId, data });
      return;
    }

    const freshAssistant = { ...realAssistant, data };
    const res = await updateAssistant(freshAssistant);
    if (res.success) {
      mutateAssistants();
    }
  };

  const onRemoveAssistant = async (assistantId) => {
    await deleteAssistant(assistantId);
    mutateAssistants();
  }

  const assistantForRent = assistants.find(a => a.moduleId === 'rent');

	return (
    <ContentLayout>
      <MainBlockLayout title='Assistants' menu={jsxMenu}>
        {assistant === 'rent' && <Rent 
          assistant={assistantForRent}
          settings={settings}
          addAssistant={data => { onAddAssistant('rent', year, data) }}
          updateAssistant={data => { onUpdateAssistant(assistantForRent._id, data) }}
          onBack={() => { setAssistant(); setSection('dailyTasks'); }}
        />}

        {!assistant && section === 'dailyTasks' && <StyledAssistantCards>
          <StyledAssistantCard>
            <Image alt="Rent" src={imgAssistantRent} style={{ width: 80, height: 'auto' }} />
            <div className="content">
            <div className="amount">¥ 3,000,000</div>
              <h3>Rent</h3>
              <p>Input your monthly rent expenses here, if you don’t have a receipt or an invoice of it.</p>
              {<Button type="primary" style={{ marginRight: 5 }} onClick={(ev) => { 
                setAssistant('rent');
                setSection('');
              }}>{!!assistantForRent ? "Modify" : "Add"}</Button>}
              {!!assistantForRent && <Button danger onClick={(ev) => { 
                // Are you sure
                if (confirm('Are you sure you want to delete this assistant and all the related data?')) {
                  onRemoveAssistant(assistantForRent._id);
                }
              }}>Delete</Button>}
            </div>
          </StyledAssistantCard>

          <StyledAssistantCard className="disabled">
            <Image alt="Banks Reconciliation" src={imgAssistantBank} style={{ width: 80, height: 'auto' }} />
            <div className="content">
              <div className="amount">¥ 3,000,000</div>
              <h3>Banks reconciliation</h3>
              <p>Check the balance of your bank accounts and cash. If it doesn’t match, add the missing inputs.</p>
            </div>
          </StyledAssistantCard>
        </StyledAssistantCards>}

        {!assistant && section === 'taxReturnTasks' && <StyledAssistantCards>
          ⛩️ 👻 ⛩️
        </StyledAssistantCards>}

      </MainBlockLayout>
    </ContentLayout>
	);
};

export default withAuth(Assistants);